import React from "react";
import { Link } from "react-router-dom";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import useReveal from "@/hooks/useReveal";

const LAST_UPDATED = "21 June 2026";
const EFFECTIVE_DATE = "21 June 2026";

/* ---------- small reusable bits ---------- */

const A = ({ href, children }) => (
  <a
    href={href}
    className="underline decoration-[var(--leo-orange)] decoration-2 underline-offset-4 hover:text-[var(--leo-orange-deep)]"
  >
    {children}
  </a>
);

const Em = ({ children }) => (
  <strong className="text-[var(--leo-ink)]">{children}</strong>
);

const SubHeading = ({ children }) => (
  <h3 className="font-display text-lg sm:text-xl text-[var(--leo-ink)] mt-6 mb-2 first:mt-0">
    {children}
  </h3>
);

const Table = ({ headers, rows }) => (
  <div className="mt-4 overflow-x-auto -mx-2 px-2">
    <table className="w-full border-collapse text-sm sm:text-base">
      <thead>
        <tr>
          {headers.map((h) => (
            <th
              key={h}
              className="text-left font-display text-[var(--leo-ink)] bg-[var(--leo-cream-deep)] px-3 py-2 align-top"
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i}>
            {row.map((cell, j) => (
              <td
                key={j}
                className="border-[2px] border-[var(--leo-ink)] px-3 py-2 align-top text-[var(--leo-ink-soft)]"
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/* ---------- policy sections ---------- */

const accents = [
  "#FFE1C4",
  "#DDE8F8",
  "#D6EEDF",
  "#FFF0CC",
  "#FBE6C8",
];

const sections = [
  {
    n: "01",
    title: "About this policy",
    body: (
      <>
        <p>
          This policy explains how Code Ninja Coaching Ltd ("we", "us", "our")
          collects, uses, stores, and protects personal data when you and your
          child use Meet Leo (the "Service"), available at meetleo.co.uk and via
          the Meet Leo iOS application.
        </p>
        <p className="mt-3">
          We take children's privacy extremely seriously. This policy is written
          to be readable. If anything is unclear, contact us at{" "}
          <A href="mailto:founder@meetleo.co.uk">founder@meetleo.co.uk</A> and we
          will explain.
        </p>
        <p className="mt-3">
          We are registered in England and Wales (Code Ninja Coaching Ltd). We
          are the data controller for personal data processed through the
          Service.
        </p>
      </>
    ),
  },
  {
    n: "02",
    title: "Who this policy applies to",
    body: (
      <>
        <p>
          Meet Leo is a voice-based AI maths companion designed for children in
          UK primary school (Years 1–6, ages 5–11).
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            <Em>Parents and legal guardians</Em> create the account, provide
            consent, and control the Service on behalf of their child.
          </li>
          <li>
            <Em>Children</Em> interact with Leo through voice during sessions.
            Children do not create accounts and cannot use the Service without a
            parent or guardian setting it up and being present.
          </li>
        </ul>
        <p className="mt-3">
          This policy covers both the parent's data and the child's data.
        </p>
      </>
    ),
  },
  {
    n: "03",
    title: "Lawful basis for processing children's data",
    body: (
      <>
        <p>
          We process children's personal data under UK GDPR Article 6(1)(a) —{" "}
          <Em>consent given by the holder of parental responsibility</Em> — and,
          where applicable, Article 6(1)(f) (legitimate interests, where such
          interests do not override the child's rights).
        </p>
        <p className="mt-3">
          We do not rely on the child's own consent. Account creation, consent to
          processing, and ongoing control of the Service sit entirely with the
          parent.
        </p>
        <p className="mt-3">
          Where we process special category data (for example, if a child
          discloses health-related information during a session), we rely on UK
          GDPR Article 9(2)(a) — explicit parental consent — or Article 9(2)(c)
          where necessary to protect the vital interests of the child.
        </p>
      </>
    ),
  },
  {
    n: "04",
    title: "What data we collect",
    body: (
      <>
        <SubHeading>4.1 Account data (parent)</SubHeading>
        <ul className="space-y-2 list-disc pl-5">
          <li>Name and email address (provided via Google Sign-In)</li>
          <li>Google account identifier</li>
          <li>
            Subscription and billing status (managed by Apple via in-app
            purchase; we do not see card details)
          </li>
          <li>Device and app version information for diagnostic purposes</li>
        </ul>

        <SubHeading>4.2 Child profile data (provided by parent)</SubHeading>
        <ul className="space-y-2 list-disc pl-5">
          <li>Child's first name</li>
          <li>Year group (school year)</li>
          <li>
            Optional: information the parent chooses to share to personalise
            Leo's interactions (for example, a pet's name, hobbies, sibling
            names)
          </li>
        </ul>

        <SubHeading>4.3 Voice and conversation data (during sessions)</SubHeading>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            <Em>Audio captured by the device microphone during a session.</Em>{" "}
            This audio is streamed in real time to our speech-to-text provider
            for transcription. We do not store the raw audio.
          </li>
          <li>
            <Em>Text transcripts</Em> of what the child says and what Leo says in
            reply. Transcripts are stored to support diagnostics, improve the
            Service, and provide parents with visibility through the parent
            dashboard. Transcripts are deleted after 90 days unless a longer
            retention is necessary to investigate a specific issue you have
            raised.
          </li>
          <li>
            <Em>Pronunciation of the child's name</Em> (text representation only,
            used by Leo to address the child correctly).
          </li>
        </ul>

        <SubHeading>4.4 Learning data</SubHeading>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            Records of maths topics covered, times tables progress, and session
            summaries.
          </li>
          <li>
            Memories Leo accumulates about the child to personalise future
            sessions (for example, "enjoys baking play-frame", "found Year 2
            fractions challenging"). Parents can view and delete these from the
            parent dashboard.
          </li>
          <li>Per-fact progress on multiplication tables.</li>
        </ul>

        <SubHeading>4.5 Safeguarding data</SubHeading>
        <p>
          If our disclosure detection layer identifies that a child has said
          something indicating possible abuse, neglect, self-harm, suicidal
          ideation, or another welfare concern, we will log the relevant
          transcript section and the category of disclosure. This data is
          reviewed by our designated safeguarding lead. See Section 9 for full
          detail.
        </p>

        <SubHeading>4.6 Session audit data</SubHeading>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            Records of parent biometric unlock attempts (success, failure, time)
            for safeguarding audit purposes.
          </li>
        </ul>

        <SubHeading>4.7 Technical and diagnostic data</SubHeading>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            App version, iOS version, device model, anonymised crash reports.
          </li>
          <li>
            Server-side logs of session start/end, errors, and performance
            metrics (not containing transcript content unless an error report
            requires it).
          </li>
        </ul>

        <SubHeading>4.8 Notifications data</SubHeading>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            If you enable parent notifications, your device push token and your
            preferred notification time/frequency.
          </li>
        </ul>

        <SubHeading>4.9 Information we do NOT collect</SubHeading>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            We do not collect children's photographs, video, or location data.
          </li>
          <li>We do not collect contact details for children.</li>
          <li>
            We do not collect data on children's friends, classmates, or other
            third parties unless a parent or child mentions them in a session (in
            which case any such reference is treated as session content and
            subject to the same retention rules).
          </li>
          <li>We do not use cookies or tracking technologies for advertising.</li>
          <li>
            <Em>We do not sell any personal data to anyone, ever.</Em>
          </li>
        </ul>
      </>
    ),
  },
  {
    n: "05",
    title: "How we use the data",
    body: (
      <>
        <p>We use personal data only for the following purposes:</p>
        <ol className="mt-3 space-y-2 list-decimal pl-5">
          <li>
            <Em>To provide the Service</Em> — running voice sessions,
            transcribing what the child says, generating Leo's responses, playing
            them back, and saving learning progress.
          </li>
          <li>
            <Em>To personalise the Service</Em> — Leo's accumulated memory of
            each child is the core of the product. This memory makes interactions
            feel continuous and supportive.
          </li>
          <li>
            <Em>To keep children safe</Em> — disclosure detection, safeguarding
            flagging, parent biometric unlock auditing.
          </li>
          <li>
            <Em>To communicate with you</Em> — service announcements, billing,
            and (if opted in) parent notifications.
          </li>
          <li>
            <Em>To diagnose and improve the Service</Em> — analysing logs and
            transcripts (within the 90-day retention window) to fix bugs and
            improve Leo's behaviour. We do not use children's voice or transcript
            data to train third-party AI models. See Section 7.
          </li>
          <li>
            <Em>To meet legal and regulatory obligations</Em> — including
            responding to lawful requests from authorities and complying with
            safeguarding obligations.
          </li>
        </ol>
        <p className="mt-3">
          We do not use any personal data for advertising, profiling for
          marketing, or automated decision-making with legal or significant
          effect on the child.
        </p>
      </>
    ),
  },
  {
    n: "06",
    title: "Third parties who process data on our behalf (sub-processors)",
    body: (
      <>
        <p>
          We use the following providers to operate the Service. Each is
          contractually bound by a Data Processing Agreement requiring them to
          process data only on our instructions and to meet UK GDPR standards.
        </p>
        <Table
          headers={["Provider", "Purpose", "Location", "Data shared"]}
          rows={[
            [
              <Em>Supabase</Em>,
              "Database hosting (account data, transcripts, learning records)",
              "EU (West)",
              "All stored data",
            ],
            [
              <Em>Railway</Em>,
              "Server hosting",
              "EU/US",
              "Server logs, in-transit data",
            ],
            [
              <Em>Deepgram</Em>,
              "Speech-to-text transcription",
              "EU (regional endpoint api.eu.deepgram.com)",
              "Audio streamed during sessions",
            ],
            [
              <Em>Anthropic</Em>,
              "AI language model (Claude) for Leo's responses and session assessment",
              "US (with Standard Contractual Clauses); contractually prohibited from training on our data",
              "Conversation text (no raw audio)",
            ],
            [
              <Em>Cartesia</Em>,
              "Text-to-speech (Leo's voice)",
              "US (with Standard Contractual Clauses)",
              "Text strings to be spoken (containing child's first name and what Leo says — no child voice audio is sent to Cartesia)",
            ],
            [
              <Em>Google (Sign-In)</Em>,
              "Authentication",
              "EU/US",
              "Parent's email, Google account identifier",
            ],
            [
              <Em>Apple</Em>,
              "In-app purchase, push notifications",
              "EU/US",
              "Apple manages billing directly; we do not see card details",
            ],
            [
              <Em>RevenueCat</Em>,
              "Subscription state management",
              "US (with Standard Contractual Clauses)",
              "Anonymised subscription identifier",
            ],
          ]}
        />
        <p className="mt-4">
          <Em>Children's voice audio is only ever sent to Deepgram</Em> for the
          purpose of converting speech to text. It is not sent to Cartesia,
          Anthropic, or any other provider. Cartesia receives only text strings
          (the script Leo speaks).
        </p>
        <p className="mt-3">
          <Em>International transfers:</Em> where data is transferred outside the
          UK, we rely on UK International Data Transfer Agreements or the UK
          Addendum to the EU Standard Contractual Clauses. A list of current
          sub-processors is available on request via{" "}
          <A href="mailto:founder@meetleo.co.uk">founder@meetleo.co.uk</A>.
        </p>
      </>
    ),
  },
  {
    n: "07",
    title: "AI model training",
    body: (
      <>
        <p>
          We do not permit our AI providers to train models on your or your
          child's data.
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            <Em>Anthropic:</Em> by their commercial terms, Anthropic is
            contractually prohibited from training their models on customer
            content.
          </li>
          <li>
            <Em>Cartesia:</Em> we have opted out of model training where opt-out
            is offered, and contractually require this where we operate under
            enterprise terms.
          </li>
          <li>
            <Em>Deepgram:</Em> we have opted out of Deepgram's Model Improvement
            Program at the API level, and are formalising a no-training
            contractual commitment as part of our Data Processing Agreement.
          </li>
        </ul>
        <p className="mt-3">
          We do not train any AI model of our own on identifiable child data. We
          may use aggregated, anonymised, and de-identified data to improve Leo's
          behaviour over time.
        </p>
      </>
    ),
  },
  {
    n: "08",
    title: "How long we keep data",
    body: (
      <Table
        headers={["Data type", "Retention"]}
        rows={[
          [
            "Account data (parent)",
            "While the account is active, plus 12 months after account closure for legal/audit purposes",
          ],
          [
            "Child profile data",
            "While the account is active; deleted on account deletion",
          ],
          ["Voice audio", "Not stored; transcribed in real time and discarded"],
          ["Session transcripts", "90 days, then automatically deleted"],
          [
            "Learning data and memories",
            "While the account is active; deletable by parent at any time",
          ],
          [
            "Safeguarding disclosure logs",
            "7 years (in line with UK statutory safeguarding record-keeping obligations)",
          ],
          ["Session unlock audit log", "12 months"],
          ["Technical logs", "30 days"],
          ["Diagnostic crash reports", "90 days"],
        ]}
      />
    ),
  },
  {
    n: "09",
    title: "Safeguarding",
    body: (
      <>
        <p>
          We take child welfare seriously. Meet Leo includes a safeguarding layer
          designed to identify if a child may be at risk during a session.
        </p>
        <SubHeading>How it works</SubHeading>
        <ul className="space-y-2 list-disc pl-5">
          <li>
            A deterministic detection layer scans what the child says for
            indicators of abuse, neglect, self-harm, suicidal ideation,
            witnessing violence, exposure to grooming, or other welfare concerns.
          </li>
          <li>
            If a concern is detected, Leo's AI response is <Em>bypassed</Em>.
            Instead, a pre-written response (developed with reference to qualified
            safeguarding guidance) is given — typically a brief acknowledgement,
            signposting to a trusted adult, and signposting to Childline (0800
            1111).
          </li>
          <li>
            The relevant transcript section and the category of concern are
            logged for review by our designated safeguarding lead.
          </li>
          <li>
            We may, where we judge it necessary to protect the child, contact the
            parent, the local authority (LADO), the NSPCC, or other appropriate
            authorities.
          </li>
          <li>
            Where the disclosure suggests the parent or guardian may be the
            source of harm, we will not notify the parent through the dashboard
            and will instead seek guidance from appropriate authorities before any
            contact.
          </li>
        </ul>
        <p className="mt-4">
          <Em>Our designated safeguarding lead</Em> is the founder, Pratik Garg,
          contactable at{" "}
          <A href="mailto:founder@meetleo.co.uk">founder@meetleo.co.uk</A>. We
          engage a qualified children's safeguarding consultant for annual review
          of our policy and detection categories.
        </p>
        <p className="mt-3">
          We are required by law to share information with authorities in certain
          safeguarding scenarios. In those scenarios, the lawful basis for sharing
          data overrides the consent basis under which it was originally
          collected.
        </p>
      </>
    ),
  },
  {
    n: "10",
    title: "Parent biometric unlock",
    body: (
      <>
        <p>
          Every voice session with Leo begins with a parent biometric
          authentication step (FaceID, TouchID, or device passcode). This is by
          design and cannot be switched off.
        </p>
        <p className="mt-3">
          The purpose is to ensure that a responsible adult is present at the
          start of each session, supporting both safeguarding and engagement. We
          log the time of each unlock attempt (success or failure) for audit. We
          do not store biometric data — authentication is handled entirely by the
          device's operating system; we receive only a pass/fail signal.
        </p>
      </>
    ),
  },
  {
    n: "11",
    title: "Your rights",
    body: (
      <>
        <p>
          Under UK GDPR, you have the following rights in respect of your data and
          your child's data. Most of these are exercisable directly through the
          parent dashboard. For others, contact{" "}
          <A href="mailto:founder@meetleo.co.uk">founder@meetleo.co.uk</A>.
        </p>
        <Table
          headers={["Right", "What it means", "How to use it"]}
          rows={[
            ["Access", "See what data we hold", "Email us; we respond within 1 month"],
            ["Rectification", "Correct inaccurate data", "Email us, or edit in the parent dashboard"],
            ["Erasure", "Delete your or your child's data", "Parent dashboard (memories); email us for full deletion"],
            ["Restriction", "Pause processing", "Email us"],
            ["Portability", "Receive your data in a portable format", "Email us"],
            ["Objection", "Object to processing on legitimate-interests grounds", "Email us"],
            ["Withdraw consent", "Withdraw consent at any time", "Email us or delete account; some data must be retained where law requires"],
            ["Complain", "Complain to a regulator (see Section 13)", "Direct to the ICO"],
          ]}
        />
        <p className="mt-4">
          We will not charge you for exercising these rights unless requests are
          manifestly unfounded or excessive.
        </p>
        <p className="mt-3">
          When you delete your account, we delete all personal data we hold within
          30 days, except where we are required by law to retain it (for example,
          safeguarding records and limited billing/audit records).
        </p>
      </>
    ),
  },
  {
    n: "12",
    title: "Data security",
    body: (
      <>
        <p>
          We protect personal data with industry-standard measures including:
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            Encryption in transit (TLS 1.2 or higher) for all data flowing between
            the app, our servers, and our sub-processors.
          </li>
          <li>Encryption at rest for all stored data in Supabase.</li>
          <li>
            Row-level security in our database to prevent cross-account access.
          </li>
          <li>
            Authentication via Google Sign-In with verified parent ownership
            before any child profile is accessible.
          </li>
          <li>Parent biometric unlock before every voice session.</li>
          <li>
            Restricted access to production data: only the founder has direct
            production database access, audited via Supabase logs.
          </li>
          <li>Regular review of sub-processor security posture.</li>
        </ul>
        <p className="mt-3">
          No system is perfectly secure. If we become aware of a personal data
          breach that risks the rights and freedoms of you or your child, we will
          notify the ICO within 72 hours where required and notify affected users
          without undue delay.
        </p>
      </>
    ),
  },
  {
    n: "13",
    title: "Regulator and complaints",
    body: (
      <>
        <p>
          If you have a concern about how we handle data, please contact us first
          at <A href="mailto:founder@meetleo.co.uk">founder@meetleo.co.uk</A>. We
          will respond within 7 days.
        </p>
        <p className="mt-3">
          You also have the right to complain to the UK Information Commissioner's
          Office (ICO):
        </p>
        <ul className="mt-3 space-y-2 list-disc pl-5">
          <li>
            Website: <A href="https://ico.org.uk">ico.org.uk</A>
          </li>
          <li>Helpline: 0303 123 1113</li>
          <li>
            Address: Information Commissioner's Office, Wycliffe House, Water
            Lane, Wilmslow, Cheshire SK9 5AF
          </li>
        </ul>
        <p className="mt-3">
          For safeguarding concerns where you believe a child is at immediate
          risk, contact the police on 999 or the NSPCC on 0808 800 5000.
        </p>
      </>
    ),
  },
  {
    n: "14",
    title: "Cookies and analytics",
    body: (
      <>
        <p>
          The Meet Leo app does not use cookies. The marketing website
          meetleo.co.uk may use a minimal set of strictly necessary cookies to
          operate. We do not use cookies for advertising, behavioural tracking, or
          third-party analytics tied to identifiable users.
        </p>
        <p className="mt-3">
          We do not use Google Analytics, Facebook Pixel, or comparable
          behavioural tracking on either the app or the website.
        </p>
      </>
    ),
  },
  {
    n: "15",
    title: "Changes to this policy",
    body: (
      <>
        <p>
          We may update this policy from time to time. Material changes will be
          notified to account holders by email at least 14 days before they take
          effect. Continued use of the Service after a change indicates
          acceptance, but you may delete your account at any time if you do not
          accept a change.
        </p>
        <p className="mt-3">
          The "Last updated" date at the top of this policy reflects the most
          recent change.
        </p>
      </>
    ),
  },
  {
    n: "16",
    title: "Children's voice and the right to be a child",
    body: (
      <>
        <p className="font-body italic">A short note, written in plain terms.</p>
        <p className="mt-3">
          Meet Leo exists because we believe children deserve a maths companion
          that respects them. That means we treat children's voices, words, and
          learning journeys with the seriousness they deserve. We do not sell
          their data. We do not show them adverts. We do not use them to train
          models we then sell to others. We do not collect more than we need. We
          delete what we can, as soon as we reasonably can.
        </p>
        <p className="mt-3">
          If you have any question, concern, or correction — for yourself or for
          your child — please email{" "}
          <A href="mailto:founder@meetleo.co.uk">founder@meetleo.co.uk</A>. We read
          every message.
        </p>
      </>
    ),
  },
  {
    n: "17",
    title: "Contact",
    body: (
      <ul className="space-y-2">
        <li>
          <span className="font-display text-[var(--leo-ink)]">
            Data controller:
          </span>{" "}
          Code Ninja Coaching Ltd
        </li>
        <li>
          <span className="font-display text-[var(--leo-ink)]">Email:</span>{" "}
          <A href="mailto:founder@meetleo.co.uk">founder@meetleo.co.uk</A>
        </li>
        <li>
          <span className="font-display text-[var(--leo-ink)]">
            Designated safeguarding lead:
          </span>{" "}
          Pratik Garg (founder)
        </li>
        <li>
          <span className="font-display text-[var(--leo-ink)]">Website:</span>{" "}
          meetleo.co.uk
        </li>
      </ul>
    ),
  },
];

export default function Privacy() {
  useReveal();

  return (
    <main
      data-testid="privacy-page"
      className="site min-h-screen pt-[72px]"
    >
      <Nav links={false} />

      {/* Hero */}
      <section className="relative max-w-3xl mx-auto px-5 sm:px-8 pt-12 sm:pt-16 pb-10 text-center">
        <span className="eyebrow eyebrow--plain mb-5">Privacy Policy · Meet Leo</span>
        <h1
          data-testid="privacy-headline"
          className="font-display text-[2.2rem] sm:text-5xl lg:text-6xl leading-[1.05] text-[var(--leo-ink)]"
        >
          Privacy,{" "}
          <span className="said">honestly</span>.
        </h1>
        <p className="mt-6 font-body text-lg sm:text-xl text-[var(--leo-ink-soft)] leading-relaxed">
          Operated by Code Ninja Coaching Ltd. We look after your child's data
          the way we'd want our own kid's data looked after. Here's exactly what
          we collect, why, and what you can ask us to do with it.
        </p>
        <p className="mt-4 font-body text-sm text-[var(--leo-ink-soft)]">
          Last updated: {LAST_UPDATED} · Effective date: {EFFECTIVE_DATE}
        </p>
      </section>

      {/* Sections */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24 space-y-8 sm:space-y-10">
        {sections.map((s, i) => (
          <article
            key={s.n}
            className="reveal sticker-soft bg-[var(--leo-paper)] p-6 sm:p-8 relative"
          >
            <div className="flex items-start gap-4 sm:gap-5">
              <div
                aria-hidden
                className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center font-display text-base sm:text-lg text-[var(--leo-ink)]"
                style={{ background: accents[i % accents.length] }}
              >
                {s.n}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-display text-2xl sm:text-3xl text-[var(--leo-ink)] leading-tight">
                  {s.title}
                </h2>
                <div className="mt-4 font-body text-base sm:text-lg text-[var(--leo-ink-soft)] leading-relaxed">
                  {s.body}
                </div>
              </div>
            </div>
          </article>
        ))}

        {/* Contact CTA */}
        <article className="reveal sticker bg-[var(--leo-cream-deep)] p-6 sm:p-8 text-center">
          <p className="font-hand text-3xl text-[var(--leo-ink)] mb-2">
            questions?
          </p>
          <h2 className="font-display text-2xl sm:text-3xl text-[var(--leo-ink)] leading-tight">
            We're real humans. Just ask.
          </h2>
          <p className="mt-4 font-body text-base sm:text-lg text-[var(--leo-ink-soft)] leading-relaxed">
            Want to see what data we hold, change it, or delete it? Drop us a
            line and we'll sort it.
          </p>
          <a
            href="mailto:founder@meetleo.co.uk"
            data-testid="privacy-contact-email"
            className="btn btn--orange mt-6"
          >
            founder@meetleo.co.uk
            <span aria-hidden>→</span>
          </a>
          <p className="mt-6 font-body text-xs text-[var(--leo-ink-soft)]">
            This policy is governed by the laws of England and Wales. UK GDPR and
            the Data Protection Act 2018 apply.
          </p>
        </article>
      </section>

      <Footer />
    </main>
  );
}
